import React, { useState, useEffect, useMemo } from 'react';
import { openReference } from '../utils/functions';
import { useNavigate } from 'react-router-dom';

// List of legible and interesting fonts
const FONTS = [
  'Playfair Display',
  'Merriweather',
  'Crimson Text',
  'Libre Baskerville',
  'Lora',
  'Source Serif Pro',
  'PT Serif',
  'Noto Serif',
  'Roboto Slab',
  'Alegreya'
];

// List of gradient backgrounds with high contrast for white text
const GRADIENTS = [
  'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',  // Dark blue to blue
  'linear-gradient(135deg, #1a2a6c 0%, #b21f1f 100%)',  // Deep navy to burgundy
  'linear-gradient(135deg, #16222A 0%, #3A6073 100%)',  // Dark slate to teal
  'linear-gradient(135deg, #232526 0%, #414345 100%)',  // Dark charcoal
  'linear-gradient(135deg, #1F1C2C 0%, #928DAB 100%)',  // Deep purple to lavender
  'linear-gradient(135deg, #2C3E50 0%, #4CA1AF 100%)',  // Dark blue to turquoise
  'linear-gradient(135deg, #1A1A2E 0%, #16213E 100%)',  // Deep navy
  'linear-gradient(135deg, #2D3436 0%, #000000 100%)',  // Dark gray to black
  'linear-gradient(135deg, #1E3C72 0%, #2A5298 100%)',  // Deep blue
  'linear-gradient(135deg, #2C5364 0%, #203A43 100%)',  // Dark teal
  'linear-gradient(135deg, #4B0082 0%, #800080 100%)',  // Deep purple to purple
  'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',  // Brown to orange-brown
  'linear-gradient(135deg, #006400 0%, #228B22 100%)',  // Dark green to forest green
  'linear-gradient(135deg, #B8860B 0%, #DAA520 100%)',  // Dark goldenrod to goldenrod
  'linear-gradient(135deg, #4B0082 0%, #9400D3 100%)',  // Indigo to dark violet
  'linear-gradient(135deg, #556B2F 0%, #6B8E23 100%)',  // Dark olive to olive drab
  'linear-gradient(135deg, #8B0000 0%, #B22222 100%)',  // Dark red to firebrick
  'linear-gradient(135deg, #483D8B 0%, #6A5ACD 100%)',  // Dark slate blue to slate blue
  'linear-gradient(135deg, #2F4F4F 0%, #708090 100%)',  // Dark slate gray to slate gray
  'linear-gradient(135deg, #800000 0%, #A52A2A 100%)'   // Maroon to brown
];

// Function to clean HTML tags from text
const cleanHtmlTags = (text) => {
  if (!text) return '';
  return text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
};

const VerseImageGenerator = ({ passages, url, insightId }) => {
  const navigate = useNavigate();
  const [verseText, setVerseText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);

  // Create a unique key for the passage
  const passageKey = useMemo(() => {
    if (!passages || passages.length === 0) return '';
    const passage = passages[0];
    return `${passage.book}-${passage.chapter}-${passage.verse1}-${url || ''}`;
  }, [passages, url]);

  useEffect(() => {
    const generateImage = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get the first passage from the array
        if (!passages || passages.length === 0) {
          throw new Error('No passages provided');
        }

        // Get the first passage object and ensure string values
        const firstPassage = {
          ...passages[0],
          chapter: String(passages[0].chapter),
          verse1: String(passages[0].verse1),
          verse2: String(passages[0].verse1) // Always use verse1
        };
        
        // Get verse text using the passage object directly
        const verseData = await openReference(firstPassage);
        if (!verseData || !verseData.body) {
          throw new Error('Failed to get verse text');
        }

        // Clean HTML tags from the verse text
        const cleanText = cleanHtmlTags(verseData.body);
        setVerseText(cleanText);

        // Create canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set canvas size (square)
        const size = 800;
        canvas.width = size;
        canvas.height = size;

        // Select random gradient
        const gradient = GRADIENTS[Math.floor(Math.random() * GRADIENTS.length)];
        
        // Create gradient background
        const bgGradient = ctx.createLinearGradient(0, 0, size, size);
        const gradientColors = gradient.match(/#[a-fA-F0-9]{6}/g);
        bgGradient.addColorStop(0, gradientColors[0]);
        bgGradient.addColorStop(1, gradientColors[1]);
        
        // Fill background
        ctx.fillStyle = bgGradient;
        ctx.fillRect(0, 0, size, size);

        // Select random font
        const font = FONTS[Math.floor(Math.random() * FONTS.length)];
        
        // Configure text
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        // Add verse text
        ctx.fillStyle = '#ffffff';
        ctx.font = `bold 36px ${font}`;
        
        // Word wrap function
        const wrapText = (text, maxWidth) => {
          const words = text.split(' ');
          const lines = [];
          let currentLine = words[0];

          for (let i = 1; i < words.length; i++) {
            const word = words[i];
            const width = ctx.measureText(currentLine + " " + word).width;
            if (width < maxWidth) {
              currentLine += " " + word;
            } else {
              lines.push(currentLine);
              currentLine = word;
            }
          }
          lines.push(currentLine);
          return lines;
        };

        // Wrap text to fit canvas
        const wrappedText = wrapText(cleanText, size * 0.8);
        
        // Draw text
        const lineHeight = 50;
        const totalHeight = wrappedText.length * lineHeight;
        const startY = (size - totalHeight) / 2;

        wrappedText.forEach((line, i) => {
          ctx.fillText(line, size / 2, startY + (i * lineHeight));
        });

        // Add verse reference
        ctx.font = `italic 24px ${font}`;
        ctx.fillText(verseData.title, size / 2, startY + (wrappedText.length * lineHeight) + 40);

        // Add URL if provided
        if (url) {
          ctx.font = `20px ${font}`;
          // Position URL near bottom of image with some padding
          ctx.fillText(url, size / 2, size - 40);
        }

        // Convert to image URL
        const imageUrl = canvas.toDataURL('image/png');
        setImageUrl(imageUrl);
      } catch (err) {
        console.error('Error generating verse image:', err);
        setError('Failed to generate verse image');
      } finally {
        setLoading(false);
      }
    };

    if (passages && passages.length > 0) {
      generateImage();
    }
  }, [passageKey]); // Only regenerate when passageKey changes

  const handleImageClick = () => {
    if (url) {
      const username = url.split('/')[1];
      const id = url.split('/')[3];
      navigate(`/${username}/insight/${id}`);
    }
  };

  return (
    <div className="verse-image-container" data-insight-id={insightId}>
      {loading ? (
        <div>Loading verse image...</div>
      ) : error ? (
        <div>Error: {error}</div>
      ) : (
        <div 
          onClick={handleImageClick}
          style={{
            width: '100%',
            maxWidth: '800px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            cursor: 'pointer',
            background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '1.2em'
          }}
        >
          {imageUrl ? (
            <img 
              src={imageUrl} 
              alt={`Verse: ${passages[0].book} ${passages[0].chapter}:${passages[0].verse1}`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '8px'
              }}
            />
          ) : (
            'Click to view insight'
          )}
        </div>
      )}
    </div>
  );
};

export default VerseImageGenerator; 